'use client';

import React, { useState, useEffect, useCallback, useRef } from 'react';
import clsx from 'clsx';
import { isFilled } from '@prismicio/helpers';
import { formatLink } from '@/app/lib/utils';
import { SmoothScrollHandler } from "@/app/lib/SmoothScrollHandler";
import { EventNavigation } from "@/app/lib/EventNavigation";
import { ConditionalLink } from '@/app/lib/ConditionalLink';
import Logo from '@/app/assets/images/logo.svg';
import LogoOnAirFest from '@/app/assets/images/logo_on_air_fest.svg';
import LogoOnAirFest10 from '@/app/assets/images/logo_on_air_fest_10.svg';
import LogoOnAirFest10Vertical from '@/app/assets/images/logo_on_air_fest_10_vertical.svg';
import LogoOnAirFest10Pink from '@/app/assets/images/logo_on_air_fest_10_pink.svg';
import LogoOnAirFest10PinkVertical from '@/app/assets/images/logo_on_air_fest_10_pink_vertical.svg';
import IconClose from '@/app/assets/images/icon_close.svg';
import IconMenu from '@/app/assets/images/icon_menu.svg';
import styles from './navigation.module.scss';

export function Navigation({ navigationData, pageData }) {
	const [showMenu, setShowMenu] = useState(false);
	const overlayRef = useRef();

	// Manage body scroll lock when menu is toggled
	useEffect(() => {
		const body = document.querySelector('body');
		if (showMenu) {
			// Calculate scrollbar width before hiding it
			const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
			body.style.overflow = 'hidden';
			body.style.paddingRight = `${scrollbarWidth}px`;
		} else {
			body.style.overflow = '';
			body.style.paddingRight = '';
		}
		return () => {
			body.style.overflow = '';
			body.style.paddingRight = '';
		};
	}, [showMenu]);

	// Toggle menu state
	const toggleMenu = useCallback(() => {
		setShowMenu((prev) => !prev);
	}, []);

	const closeMenu = (event) => {
		if (!overlayRef.current.contains(event.target)) setShowMenu(false);
	};

	const getLogoUrl = (fallback) => (
		isFilled.link(pageData?.data?.logo_url) ? pageData.data.logo_url : fallback
	);

	const defaultOafLogoUrl = navigationData?.data?.promoted_event_link || formatLink('/');
	const defaultLogoUrl = formatLink('/');

	return (
		<div className={clsx(styles.wrapper, pageData?.data?.slices6?.length ? styles.oaf : '')}>

			<SmoothScrollHandler />

			{(pageData?.data?.logo === 'On Air Fest 10') ? (
				<ConditionalLink url={getLogoUrl(defaultOafLogoUrl)} className={clsx(styles.logo, styles.oaf10)}>
					<LogoOnAirFest10 className={styles.horizontal} />
					<LogoOnAirFest10Vertical className={styles.vertical} />
				</ConditionalLink>
			) : (pageData?.data?.logo === 'On Air Fest 10 - Desktop only') ? (
				<ConditionalLink url={getLogoUrl(defaultOafLogoUrl)} className={clsx(styles.logo, styles.oaf10)}>
					{(pageData?.data?.on_air_fest || pageData?.data?.show_oaf_logo) ? (
						<LogoOnAirFest className={styles.mobileOnly} />
					) : (
						<Logo className={styles.mobileOnly} />
					)}
					<LogoOnAirFest10 className={styles.horizontal} />
				</ConditionalLink>
			) : (pageData?.data?.logo === 'On Air Fest 10 Pink') ? (
				<ConditionalLink url={getLogoUrl(defaultOafLogoUrl)} className={clsx(styles.logo, styles.oaf10Pink)}>
					<LogoOnAirFest10Pink className={styles.horizontal} />
					<LogoOnAirFest10PinkVertical className={styles.vertical} />
				</ConditionalLink>
			) : (pageData?.data?.on_air_fest || pageData?.data?.show_oaf_logo || pageData?.data?.logo === 'On Air Fest') ? (
				<ConditionalLink url={getLogoUrl(defaultOafLogoUrl)} className={styles.logo}>
					<LogoOnAirFest />
				</ConditionalLink>
			) : (
				<ConditionalLink url={getLogoUrl(defaultLogoUrl)} className={styles.logo}>
					<Logo />
				</ConditionalLink>
			)}

			<EventNavigation pageData={pageData} />

			<div onClick={toggleMenu} className={clsx(styles.toggle, pageData?.data?.on_air_fest && styles.oaf)}>
				{showMenu ? <IconClose /> : <IconMenu />}
			</div>
			<div className={clsx(styles.overlay, showMenu && styles.visible, !showMenu && styles.hiding)} onClick={closeMenu}>
				<div className={clsx(styles.overlayContainer)} ref={overlayRef}>

					{/* Section: Main */}
					<div className={clsx(styles.main)}>
						{navigationData?.data?.slices?.length && (
							<nav className={clsx(styles.nav, styles.main_nav)}>
								<ul className={clsx(styles.list)}>
									{navigationData?.data?.slices.map((slice, index) => {

										// Check if the menu item link or squadup_event_id is filled
										if (isFilled.link(slice?.primary?.menu_item) || isFilled.keyText(slice?.primary?.squadup_event_id)) {
											return (
												<li className={clsx(styles.li)} key={index}>
													<ConditionalLink
														url={slice.primary.menu_item}
														squadupeventid={slice?.primary?.squadup_event_id} // Pass squadup_event_id
														className={clsx(styles.link, styles[slice?.primary?.menu_item?.variant])}
													>
														<span>{slice?.primary?.menu_item?.text}</span>
													</ConditionalLink>
													{slice?.primary?.sub_menu?.length ? (
														<nav className={clsx(styles.nav, styles.sub_nav)}>
															<ul className={clsx(styles.list)}>
																{slice?.primary?.sub_menu.map((item, index) => {
																	// Check if the sub menu item link or squadup_event_id is filled
																	if (isFilled.link(item?.sub_menu_item) || isFilled.keyText(item?.squadup_event_id)) {
																		return (
																			<li className={clsx(styles.li)} key={index}>
																				<ConditionalLink
																					url={item.sub_menu_item}
																					squadupeventid={item?.squadup_event_id} // Pass squadup_event_id
																					className={clsx(styles.link, styles[item?.sub_menu_item?.variant])}>
																					{item?.sub_menu_item?.text}
																				</ConditionalLink>
																			</li>
																		);
																	}
																	return null;
																})}
															</ul>
														</nav>
													) : null}
												</li>
											);
										}
										return null;
									})}
								</ul>
							</nav>
						)}
					</div>

				</div>
			</div>
		</div>
	);
}